/**
 * The substantiation record: what the model was told, and what was published.
 *
 * This is compliance material rather than a debugging aid. AEGIS is not a
 * registered investment adviser and its output must be descriptive rather than
 * directive — enforced in the output schema and a validation layer, not in a
 * prompt. That enforcement is only worth anything if it can be **shown**, and
 * this is where it is shown: for each run, the signals the model was permitted
 * to cite, what it produced, and what the validation layer refused.
 *
 * **`rejected` is the interesting half.** An accepted annotation is the product
 * working. A rejected one is the guardrail doing its job, and the reason string
 * says which rule caught it.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, fmtUsd, type AiAuditRecord } from '../../lib/api.js';
import type { AiAuditStats, ResolvedRange } from '@aegis/api-client';
import { RangePicker } from '../../components/RangePicker.js';

const OUTCOME_TONE: Record<string, string> = {
  published_with_model: 'chip-ok',
  published_layer1_only: '',
  rejected_regenerated: 'chip-caution',
  rejected_fell_back: 'chip-caution',
  error: 'chip-breached',
};

export function AiAuditScreen(): JSX.Element {
  const [records, setRecords] = useState<readonly AiAuditRecord[] | null>(null);
  const [stats, setStats] = useState<AiAuditStats | null>(null);
  const [window, setWindow] = useState<ResolvedRange | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [symbol, setSymbol] = useState('');
  // 7 days rather than `all`, because the useful question on this screen is
  // "what has the model been doing lately" and `all` is now ninety days.
  const [range, setRange] = useState('7d');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (sym: string, r: string) => {
    try {
      const res = await api.aiAudit({ ...(sym ? { symbol: sym } : {}), range: r });
      setRecords(res.records);
      setStats(res.stats);
      setWindow(res.range);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404
        ? 'This is not available on your account.'
        : 'Could not load the audit log.');
    }
  }, []);
  useEffect(() => { void load(symbol, range); }, [load, range, symbol]);

  if (!records) {
    return (
      <div className="scroll-area">
        <h2>AI audit</h2>
        <p className="dim">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>AI audit</h2>
        <span className="chip">{records.length} runs</span>
      </div>

      <p className="panel-body dim">
        For each analysis: the signals the model was permitted to cite, what it
        produced, and what the validation layer refused. The rejected list is
        the guardrail working — AEGIS is not a registered adviser and its output
        is descriptive by enforcement rather than by intention. Records are kept
        for 90 days and survive a restart — this used to be an in-memory log,
        which meant every deploy erased the spend history and the rejection rate.
      </p>

      <RangePicker value={range} onChange={setRange} resolved={window} />

      {/* The aggregate for the window shown, not for the process. A spend
          figure describing a different period from the rows beneath it would
          be worse than none. */}
      {stats && stats.records > 0 && (
        <p className="panel-body dim">
          {stats.modelCalls} model call{stats.modelCalls === 1 ? '' : 's'} ·{' '}
          {fmtUsd(String(stats.totalCostUsd))} · {stats.annotationsRejected} annotation
          {stats.annotationsRejected === 1 ? '' : 's'} refused
          {stats.annotationsAccepted + stats.annotationsRejected > 0
            && ` (${Math.round(stats.rejectionRate * 100)}% rejection rate)`}
          {stats.errors > 0 && ` · ${stats.errors} error${stats.errors === 1 ? '' : 's'}`}
        </p>
      )}

      <form
        className="admin-filter"
        onSubmit={(e) => { e.preventDefault(); void load(symbol.trim().toUpperCase(), range); }}
      >
        <input
          className="input"
          placeholder="Filter by symbol — ES, NQ…"
          value={symbol}
          onChange={(e) => { setSymbol(e.target.value); }}
          aria-label="Filter the audit log by symbol"
        />
        <button type="submit" className="btn btn-quiet compact">Filter</button>
      </form>

      {records.length === 0 ? (
        <p className="panel-body">
          Nothing in this window. Records are kept for 90 days — widen the range
          or clear the symbol filter.
        </p>
      ) : (
        <ul className="admin-list">
          {records.map((r) => (
            <li key={r.id} className="admin-group">
              <button
                type="button"
                className="admin-head"
                onClick={() => { setOpen(open === r.id ? null : r.id); }}
                aria-expanded={open === r.id}
              >
                <span className={`chip ${OUTCOME_TONE[r.outcome] ?? ''}`}>
                  {r.outcome.replace(/_/g, ' ')}
                </span>
                <span className="admin-name">{r.symbol}</span>
                <span className="admin-meta">
                  {r.modelId ?? 'layer 1 only'}
                  {r.effort ? ` · ${r.effort}` : ''}
                  {' · '}
                  {r.accepted.length} kept
                  {r.rejected.length > 0 ? ` · ${r.rejected.length} dropped` : ''}
                </span>
                <span className="mono">{fmtUsd(r.costUsd.toFixed(4))}</span>
                <span className="admin-when">
                  {new Date(r.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </button>

              {open === r.id && (
                <div className="admin-detail">
                  <dl className="kv">
                    <span className="kv-pair"><dt>Trigger</dt><dd className="mono">{r.trigger}</dd></span>
                    <span className="kv-pair">
                      <dt>Why</dt><dd className="mono">{r.triggerReasons.join(', ') || '—'}</dd>
                    </span>
                    <span className="kv-pair">
                      <dt>Routing</dt><dd className="mono">{r.routingReason}</dd>
                    </span>
                    <span className="kv-pair">
                      <dt>Prompt / schema</dt>
                      <dd className="mono">{r.promptVersion} / {r.schemaVersion}</dd>
                    </span>
                    <span className="kv-pair">
                      <dt>Latency</dt>
                      <dd className="mono">
                        {r.firstTokenMs ?? '—'} ms to first token, {r.totalMs ?? '—'} ms total
                      </dd>
                    </span>
                  </dl>

                  {/* The universe the model was allowed to cite. An annotation
                      naming anything outside this list is one the validation
                      layer should have caught. */}
                  <p className="admin-label">SIGNALS OFFERED · {r.inputSignalIds.length}</p>
                  <p className="mono admin-universe">{r.inputSignalIds.join('  ') || '—'}</p>

                  {r.rejected.length > 0 ? (
                    <>
                      <p className="admin-label">REFUSED BY VALIDATION · {r.rejected.length}</p>
                      <ul className="admin-reasons">
                        {r.rejected.map((x, i) => (
                          <li key={`${r.id}-rej-${i}`}>{x.reason}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {r.error ? (
                    <>
                      <p className="admin-label">ERROR</p>
                      <pre className="admin-pre">{r.error}</pre>
                    </>
                  ) : null}

                  {r.rawOutput ? (
                    <>
                      <p className="admin-label">RAW MODEL OUTPUT</p>
                      <pre className="admin-pre">{r.rawOutput}</pre>
                    </>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
